import { toJS } from 'mobx';
import type {
  ActivityCalendar,
  ActivityParticipationLocation,
  ActivityPlan,
  ActivityPlanStartTime,
  ActivityPlanUnit,
  CurrencyCurrency,
  Product,
  TimeAvailability,
  ValidityPeriod
} from '@linktivity/ars-neo';
import {
  BaseAvailabilityStatus,
  BaseConfirmationType,
  PlanUnitUnitType,
  OptionProduct,
  BaseAllotmentStatus,
  ValidityPeriodType
} from '@linktivity/ars-neo';
import type { TimeSlotOption } from '@link/share/business/CollapsibleCard/CollapsibleCard.types';
import type { SelectedOption as SelectedOptionType } from '@link/share/business/OptionCard/OptionCard.types';
import type { TimeSlotItem } from '@link/share/business/PlanCard/PlanCard.types';
import type { PlanPriceInfo } from '@link/share/business/PriceTableDialog/PriceTableDialog.types';
import type { PaymentData } from '@link/share/components/Payment/Payment.types';
import { ALLOTMENT_STATUS } from '@link/share/constants';
import {
  calculateAllotmentStatus,
  computeValidityExpiration,
  formatTime,
  getActivityCategoryTags,
  hasSellableAllotment,
  isDefined,
  isOpenDatePlan,
  isStartTimeExpired,
  isTimePlanAvailabilitySellable,
  resolveUnitAmount,
  type ActivityCategoryTranslator
} from '@link/share/utils';
import type { PlanState } from './planState';

type DailyAvailability = NonNullable<
  ActivityCalendar['dailyAvailabilities']
>[number];

/**
 * Whether the given calendar day is bookable for the plan, i.e. the day
 * status is OK, the day's own allotment is sellable (a status-OK day with
 * zero allotment renders as sold out on the calendar), and the plan-specific
 * status is OK.
 */
export const isPlanDayAvailable = (
  day: DailyAvailability | undefined,
  planId: string
): boolean => {
  // Without a real plan id, match nothing: an empty id would otherwise match
  // calendar entries whose `planId` is also empty, which is never intended.
  if (!planId || !day) return false;
  return (
    day.status === BaseAvailabilityStatus.AvailabilityStatusOk &&
    hasSellableAllotment(day.allotmentStatus, day.allotmentQuantity) &&
    !!day.planInfos?.some(
      info =>
        info.planId === planId &&
        info.status === BaseAvailabilityStatus.AvailabilityStatusOk
    )
  );
};

/**
 * Finds the first calendar day on which the given plan is bookable. Shared by
 * the collapsible plan builder and the initial plan-state seeding so every
 * plan defaults to its own first available date (not just the first/active
 * plan).
 */
export const findFirstAvailablePlanDay = (
  dailyAvailabilities: DailyAvailability[],
  planId: string
): DailyAvailability | undefined =>
  dailyAvailabilities.find(day => isPlanDayAvailable(day, planId));

/**
 * Whether the plan's only start time is an id-only placeholder (no title and
 * no time). Such plans have no real time dimension: no slot picker renders
 * and the single-startTime effect auto-selects the placeholder id.
 */
export const hasSingleIdOnlyStartTime = (plan?: ActivityPlan): boolean => {
  const startTimes = plan?.startTimes;
  if (startTimes?.length !== 1) return false;
  return !startTimes[0].title && !startTimes[0].time;
};

/**
 * Minimal shape of unit pricing information needed by payment calculations.
 * Extracted from calendar API responses without type assertion.
 */
interface UnitPriceInfo {
  id?: string;
  amount?: number;
  displayAmount?: number;
}

export function findTimeAvailability(
  startTime?: ActivityPlanStartTime,
  timeAvailabilities?: TimeAvailability[]
): TimeAvailability | undefined {
  if (!startTime) return undefined;
  const formattedTime = formatTime(startTime.time?.trim() ?? '');
  return timeAvailabilities?.find(
    item => item.time === formattedTime || item.time === startTime.title
  );
}

/**
 * Whether the start time is bookable per the day's time availabilities: the
 * time-level status is OK and, when per-plan availability entries exist, the
 * plan's own entry is sellable per `isTimePlanAvailabilitySellable` (a
 * status-OK slot with zero allotment renders as sold out in the time-slot
 * chips). Days without a matching time entry count as not bookable.
 */
export function isStartTimeBookable(
  startTime: ActivityPlanStartTime,
  planId: string | undefined,
  timeAvailabilities: TimeAvailability[] | undefined
): boolean {
  const timeAvailability = findTimeAvailability(startTime, timeAvailabilities);
  if (
    timeAvailability?.status !== BaseAvailabilityStatus.AvailabilityStatusOk
  ) {
    return false;
  }
  const planAvailabilities = timeAvailability.planAvailabilities || [];
  if (planAvailabilities.length === 0) return true;
  return isTimePlanAvailabilitySellable(
    planAvailabilities.find(item => item.planId === planId)
  );
}

/**
 * Whether the plan can actually be booked on the given calendar day: the day
 * is available for the plan (`isPlanDayAvailable`) and — when the plan has
 * real start times — at least one of them is bookable on it and not already
 * past its start (Japan time), so today never counts as bookable once every
 * remaining slot has started. An id-only single start time has no real time
 * dimension (the single-startTime effect auto-selects it), so only day-level
 * availability applies, same as a plan without start times.
 */
export const isPlanBookableOnDay = (
  plan: ActivityPlan,
  day: DailyAvailability | undefined
): boolean => {
  const planId = plan.id || '';
  if (!isPlanDayAvailable(day, planId)) return false;
  const startTimes = plan.startTimes || [];
  return (
    startTimes.length === 0 ||
    hasSingleIdOnlyStartTime(plan) ||
    startTimes.some(
      st =>
        isStartTimeBookable(st, planId, day?.timeAvailabilities) &&
        !isStartTimeExpired(day?.date ?? '', st.time ?? '')
    )
  );
};

export interface BuildPaymentDataOptions {
  activePlanId?: string;
  activePlanState?: PlanState;
  plans: ActivityPlan[];
  unitItemsMap: Map<string | undefined, UnitPriceInfo>;
  selectedOptions: SelectedOptionType[];
  optionProducts: OptionProduct[];
  showOtherCurrencies: boolean;
  /**
   * The date a validity period counted from today starts on. Omitted, the
   * current date. Null when today is not known yet (server render and the
   * render that hydrates): such a period then shows its description only.
   */
  today?: Date | null;
}

/** An open-date plan's validity text; see `BuildPaymentDataOptions.today`. */
const validityText = (
  validityPeriod: ValidityPeriod | undefined,
  today: Date | null | undefined
) =>
  today === null && validityPeriod?.type !== ValidityPeriodType.FixedDate
    ? validityPeriod?.description || ''
    : computeValidityExpiration(validityPeriod, today ?? undefined);

export function buildPaymentData({
  activePlanId,
  activePlanState,
  plans,
  unitItemsMap,
  selectedOptions,
  optionProducts,
  showOtherCurrencies,
  today
}: BuildPaymentDataOptions): PaymentData {
  const selectedUnits = activePlanState?.selectedUnits ?? [];
  const activePlan = plans.find(p => p.id === activePlanId);
  const planTitle = activePlan?.title || '';
  const isOpenDate = isOpenDatePlan(activePlan);
  const validityExpiration = isOpenDate
    ? validityText(activePlan?.validityPeriod, today)
    : '';

  const tickets = selectedUnits
    .filter(unit => unit.quantity !== undefined && unit.quantity > 0)
    .map(unit => {
      const currentUnit = unitItemsMap.get(unit.id);
      return {
        label: unit.name || '',
        price: resolveUnitAmount(currentUnit ?? unit, showOtherCurrencies),
        quantity: unit.quantity || 0
      };
    });

  const planSection =
    activePlanId && activePlanState
      ? {
          name: planTitle,
          ...(isOpenDate
            ? { validityPeriod: validityExpiration }
            : {
                dateTime: `${activePlanState.selectedDate ?? ''} ${
                  activePlanState.selectedTimeTitle ?? ''
                }`
              }),
          items: tickets
        }
      : undefined;

  const optionProductById = new Map<string, OptionProduct>();
  for (const op of optionProducts) {
    const id = op.productContent?.id;
    if (id) optionProductById.set(id, op);
  }

  const optionSections = selectedOptions
    .filter(opt => opt.displayInfo.totalQuantity > 0)
    .map(opt => {
      const optionPlan = optionProductById
        .get(opt.productContentId)
        ?.productContent?.activity?.plans?.find(
          p => p.id === opt.activityForm.planId
        );
      const optionIsOpenDate = isOpenDatePlan(optionPlan);

      return {
        name: opt.displayInfo.title,
        planName: optionPlan?.title || '',
        ...(optionIsOpenDate
          ? {
              validityPeriod: validityText(optionPlan?.validityPeriod, today)
            }
          : {
              dateTime: `${opt.targetDate ?? ''} ${
                opt.startTime ? formatTime(opt.startTime, 'HH:mm') : ''
              }`
            }),
        items: opt.unitQuantities
          .filter(u => u.count !== undefined && u.count > 0)
          .map(u => ({
            label: u.name || '',
            price: resolveUnitAmount(u, showOtherCurrencies),
            quantity: u.count || 0
          }))
      };
    });

  const ticketsTotal = tickets.reduce(
    (sum, item) => sum + (item.price || 0) * item.quantity,
    0
  );
  const optionsTotal = optionSections.reduce(
    (sum, section) =>
      sum +
      section.items.reduce(
        (itemSum, item) => itemSum + item.price * item.quantity,
        0
      ),
    0
  );

  return {
    plan: planSection,
    options: optionSections,
    totalAmount: ticketsTotal + optionsTotal
  };
}

export interface BuildCollapsiblePlansOptions {
  plans: ActivityPlan[];
  dailyAvailabilities: DailyAvailability[];
  planStates: Map<string, PlanState>;
  cancellationPolicy: string;
  currency: CurrencyCurrency;
  showOtherCurrencies: boolean;
  confirmationType?: BaseConfirmationType;
}

/**
 * A catalog unit normalized by `buildCollapsiblePlans`: id/name default to ''
 * and the booking flags to false/true, so consumers (e.g. the strict
 * `canBeBookingIndepend` checks in `getDependentUnitWarningTitle`) can rely
 * on them being present. Unlike `UnitCardUnit`, it carries no `quantity` —
 * selection state lives in `PlanState.selectedUnits`.
 */
export type CollapsiblePlanUnit = ActivityPlanUnit & {
  id: string;
  name: string;
  includeCapacity: boolean;
  canBeBookingIndepend: boolean;
};

/** One entry of the `collapsiblePlans` array driving the plan sections. */
export interface CollapsiblePlan {
  activityPlan: ActivityPlan;
  /** First time-slot label suffixed with 〜, '' when the plan has none. */
  time: string;
  priceInfo: {
    amount: number;
    currencyCode: CurrencyCurrency;
  };
  priceLabel: string;
  units: CollapsiblePlanUnit[];
  /** The plan's selected date, '' or undefined when none is selected yet. */
  defaultTargetDate: string | undefined;
  /**
   * The plan's selected time-slot id, '' when none. Drives the card's
   * controlled time Select, so programmatic clears/changes (activation-date
   * moves, option-sold-out re-point) reach the UI.
   */
  selectedTimeSlot: string;
  timeSlots: TimeSlotOption[];
  /**
   * All start times (sold-out included) with per-slot allotment status, for
   * the column layout's time-slot chips. Unlike `timeSlots`, nothing is
   * filtered out.
   */
  startTimes: TimeSlotItem[];
  meetingPlace: ActivityParticipationLocation | undefined;
  cancellationPolicy: string;
  hideDateAndTime: boolean;
  /**
   * Whether this plan books against a date the calendar puts on screen. False
   * for the two kinds that book without one — a lottery (product-wide) and an
   * open-date plan (per-plan) — so a view never has to compose those two halves
   * itself and get one of them wrong.
   */
  showsCalendarDate: boolean;
}

export function buildCollapsiblePlans({
  plans,
  dailyAvailabilities,
  planStates,
  cancellationPolicy,
  currency,
  showOtherCurrencies,
  confirmationType
}: BuildCollapsiblePlansOptions): CollapsiblePlan[] {
  const hideDateAndTime =
    confirmationType === BaseConfirmationType.ConfirmationTypeLottery;
  return plans.map(plan => {
    const firstAvailableDay = findFirstAvailablePlanDay(
      dailyAvailabilities,
      plan.id || ''
    );

    const planInfo = firstAvailableDay?.planInfos?.find(
      p => p.planId === plan.id
    );
    const planState = planStates.get(plan.id || '');
    const selectedDate = planState?.selectedDate;
    const dayAvailability = dailyAvailabilities.find(
      d => d.date === selectedDate
    );
    const planUnitItems = selectedDate
      ? dayAvailability?.planInfos?.find(p => p.planId === plan.id)
          ?.unitItems || []
      : planInfo?.unitItems || [];

    const cheapestPriceUnits = planUnitItems.filter(
      u =>
        u?.includeCheapestPrice && (u.amount != null || u.displayAmount != null)
    );
    const displayUnit =
      cheapestPriceUnits.length > 0
        ? cheapestPriceUnits.reduce((min, u) =>
            resolveUnitAmount(u, showOtherCurrencies) <
            resolveUnitAmount(min, showOtherCurrencies)
              ? u
              : min
          )
        : planUnitItems.find(
            u =>
              (u?.unitType ?? '') === PlanUnitUnitType.AgeAdult ||
              u?.unitType === PlanUnitUnitType.AgeAdultAndChild
          );

    // A slot already past its start (Japan time) is unbookable exactly like a
    // sold-out one: dropped from the dropdown options and rendered sold-out
    // (disabled) in the PlanCard chips. No selected date means no expiry
    // (isStartTimeExpired returns false on an empty date), mirroring the
    // option drawer's startTimeOptions.
    const filteredStartTimes = (plan.startTimes || []).filter(
      st =>
        isStartTimeBookable(st, plan.id, dayAvailability?.timeAvailabilities) &&
        !isStartTimeExpired(selectedDate ?? '', st.time ?? '')
    );
    const timeSlots = filteredStartTimes.map(st => ({
      label: st.title || formatTime(st.time ?? '') || '',
      value: st.id || ''
    }));

    const startTimes: TimeSlotItem[] = (plan.startTimes || []).map(st => {
      const timeAvailability = findTimeAvailability(
        st,
        dayAvailability?.timeAvailabilities
      );
      const isTimeAvailable =
        timeAvailability?.status ===
        BaseAvailabilityStatus.AvailabilityStatusOk;
      const planAvailabilities = timeAvailability?.planAvailabilities;
      const timePlanAvailability = planAvailabilities?.find(
        item => item.planId === plan.id
      );
      const isUnlimited =
        timePlanAvailability?.allotmentStatus ===
        BaseAllotmentStatus.AllotmentStatusUnlimited;
      // Calendars without per-plan availability entries only carry the
      // time-level status; mirror the `timeSlots` filter above and trust it.
      const status = isStartTimeExpired(selectedDate ?? '', st.time ?? '')
        ? ALLOTMENT_STATUS.SOLD_OUT
        : planAvailabilities?.length
          ? calculateAllotmentStatus(
              isTimeAvailable,
              timePlanAvailability?.allotmentStatus ===
                BaseAllotmentStatus.AllotmentStatusAllocable || isUnlimited,
              timePlanAvailability?.allotmentQuantity ?? 0,
              isUnlimited
            )
          : isTimeAvailable
            ? ALLOTMENT_STATUS.ALLOCABLE
            : ALLOTMENT_STATUS.SOLD_OUT;
      return {
        id: st.id || '',
        title: st.title || '',
        time: st.time || '',
        status
      };
    });

    return {
      activityPlan: plan,
      time: timeSlots.length > 0 ? `${timeSlots[0].label}〜` : '',
      priceInfo: {
        amount: resolveUnitAmount(displayUnit, showOtherCurrencies),
        currencyCode: currency
      },
      priceLabel: displayUnit?.name || '',
      units: planUnitItems.map(u => ({
        ...toJS(u),
        id: u.id || '',
        name: u.name || '',
        includeCapacity: u.includeCapacity || false,
        canBeBookingIndepend: u.canBeBookingIndepend ?? true
      })),
      defaultTargetDate: selectedDate,
      selectedTimeSlot: planState?.selectedTimeId ?? '',
      timeSlots,
      startTimes,
      meetingPlace: plan.participationLocations?.[0],
      cancellationPolicy,
      hideDateAndTime,
      showsCalendarDate: !hideDateAndTime && !isOpenDatePlan(plan)
    };
  });
}

/**
 * Per-plan unit pricing for the given day, shaped for `PriceTableDialog`.
 */
export function buildPlanPriceInfos(
  plans: ActivityPlan[],
  dailyAvailability: DailyAvailability | undefined
): PlanPriceInfo[] {
  return (
    dailyAvailability?.planInfos?.map(planInfo => {
      const activityPlan = plans.find(p => p.id === planInfo.planId);
      return {
        planId: planInfo.planId || '',
        planTitle: activityPlan?.title || '',
        unitItems: planInfo.unitItems || []
      };
    }) || []
  );
}

export interface ProductDisplayInfo {
  optionProducts: NonNullable<Product['optionProducts']>;
  hasConfirmationPopup: boolean;
  tags: string[];
  images: string[];
  productInfo: {
    title: string;
    tags: string[];
    description: string;
    highlight: string;
  };
  cancelPolicy: { title: string; description: string };
}

export function deriveProductDisplayInfo(
  product: Product | undefined,
  translate: ActivityCategoryTranslator
): ProductDisplayInfo {
  const activity = product?.productContent?.activity;
  // Tags come from the activity categories, matching the product cards; the
  // separately curated `product.tags` are not surfaced.
  const tags = getActivityCategoryTags(activity?.categories, translate);

  return {
    optionProducts:
      product?.optionProducts
        ?.filter(isDefined)
        .filter(item => item.productContent != null) ?? [],
    hasConfirmationPopup: isDefined(product?.bookingConfirmationPopup),
    tags,
    images: activity?.imageUrls ?? [],
    productInfo: {
      title: activity?.title ?? '',
      tags,
      description: activity?.description ?? '',
      // Sits on productContent itself, not on the activity payload.
      highlight: product?.productContent?.highlight ?? ''
    },
    cancelPolicy: {
      title: activity?.cancelPolicy?.title ?? '',
      description: activity?.cancelPolicy?.description ?? ''
    }
  };
}

export interface ComputeMaxCapacityOptions {
  activePlanId?: string;
  activePlan?: ActivityPlan;
  selectedTimeId?: string;
  timeAvailabilities: DailyAvailability['timeAvailabilities'];
}

export function computeMaxCapacity({
  activePlanId,
  activePlan,
  selectedTimeId,
  timeAvailabilities
}: ComputeMaxCapacityOptions): number {
  const planMaxCapacity = activePlan?.maxCapacityInclusive || 0;

  if (!selectedTimeId || !activePlanId || !activePlan) return planMaxCapacity;

  const startTime = activePlan.startTimes?.find(st => st.id === selectedTimeId);
  if (!startTime) return planMaxCapacity;

  const timeAvailability = findTimeAvailability(startTime, timeAvailabilities);
  const planAvailability = timeAvailability?.planAvailabilities?.find(
    item => item.planId === activePlanId
  );
  const allotmentQuantity = planAvailability?.allotmentQuantity;

  // Unlimited inventory carries no meaningful quantity (it arrives as 0), so
  // it must never clamp the capacity.
  if (
    allotmentQuantity === undefined ||
    allotmentQuantity === null ||
    planAvailability?.allotmentStatus ===
      BaseAllotmentStatus.AllotmentStatusUnlimited
  ) {
    return planMaxCapacity;
  }

  return planMaxCapacity > 0
    ? Math.min(planMaxCapacity, allotmentQuantity)
    : allotmentQuantity;
}
